import { db, CallFoundryDexie } from '../db';
import {
  getLocalLearnerId,
  isLocalLearner,
  setAuthenticatedUserId,
  LEGACY_ANONYMOUS_USER_ID,
} from '../identity/learnerIdentity';
import type { SyncQueueItem } from '../models';

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export class MigrationService {
  private dexie: CallFoundryDexie;

  constructor(databaseInstance: CallFoundryDexie = db) {
    this.dexie = databaseInstance;
  }

  /**
   * Promotes unlinked local training events to an authenticated user account.
   *
   * STRICT ARCHITECTURAL INVARIANTS:
   * 1. CanonicalTrainingEvent is NEVER rewritten or mutated during account linking.
   *    Its historical evidence and originLearnerId remain 100% immutable.
   * 2. Account ownership is purely transport/replication metadata stored in syncMetadata and syncQueue.
   * 3. Events already associated with a different authenticated account (e.g. Account A)
   *    are NEVER transferred or re-associated with another account (e.g. Account B).
   */
  async promoteAnonymousEventsToAccount(newUserId: string): Promise<{
    migratedEvents: number;
    queuedForSync: number;
  }> {
    if (!newUserId || isLocalLearner(newUserId)) {
      return { migratedEvents: 0, queuedForSync: 0 };
    }

    const localLearnerId = getLocalLearnerId();

    return await this.dexie.transaction(
      'rw',
      [
        this.dexie.events,
        this.dexie.learnerProfiles,
        this.dexie.userSettings,
        this.dexie.syncQueue,
        this.dexie.syncMetadata,
      ],
      async () => {
        // 1. Ensure any legacy events lacking syncMetadata are initialized for unlinked promotion
        const allEvents = await this.dexie.events.toArray();
        for (const ev of allEvents) {
          const existingMeta = await this.dexie.syncMetadata.get(ev.eventId);
          if (!existingMeta) {
            await this.dexie.syncMetadata.put({
              eventId: ev.eventId,
              status: 'pending',
              localAckAt: ev.clientCreatedAt || new Date().toISOString(),
              accountUid: undefined,
            });
          }
        }

        // 2. Fetch all syncMetadata records that are NOT already owned by a remote account
        const allMetadata = await this.dexie.syncMetadata.toArray();
        const unlinkedMetadata = allMetadata.filter(
          (m) => !m.accountUid || isLocalLearner(m.accountUid)
        );

        let count = 0;

        // 2. Retarget transport replication to new user account WITHOUT mutating canonical events
        for (const meta of unlinkedMetadata) {
          // Verify corresponding event exists locally
          const eventExists = await this.dexie.events.get(meta.eventId);
          if (!eventExists) continue;

          // Update transport metadata in syncMetadata (CanonicalTrainingEvent remains untouched!)
          await this.dexie.syncMetadata.put({
            ...meta,
            accountUid: newUserId,
            status: 'pending',
          });

          // Add to or update sync queue
          const existingQueue = await this.dexie.syncQueue
            .where('eventId')
            .equals(meta.eventId)
            .first();

          if (!existingQueue) {
            const queueItem: SyncQueueItem = {
              id: generateUUID(),
              eventId: meta.eventId,
              accountUid: newUserId,
              userId: newUserId,
              attempts: 0,
              lastAttemptAt: null,
              status: 'pending',
              createdAt: new Date().toISOString(),
            };
            await this.dexie.syncQueue.put(queueItem);
          } else {
            await this.dexie.syncQueue.update(existingQueue.id, {
              accountUid: newUserId,
              userId: newUserId,
              status: 'pending',
            });
          }

          count++;
        }

        // 3. Promote learner profile if exists
        const localProfile =
          (await this.dexie.learnerProfiles.get(localLearnerId)) ??
          (await this.dexie.learnerProfiles.get(LEGACY_ANONYMOUS_USER_ID));

        if (localProfile) {
          const updatedProfile = { ...localProfile, userId: newUserId };
          await this.dexie.learnerProfiles.put(updatedProfile);
          if (localProfile.userId !== newUserId) {
            await this.dexie.learnerProfiles.delete(localProfile.userId);
          }
        }

        // 4. Update user settings active identity
        const currentSettings = await this.dexie.userSettings.get('current_user_settings');
        if (currentSettings) {
          await this.dexie.userSettings.update('current_user_settings', {
            userId: newUserId,
          });
        }

        // 5. Update runtime active learner state
        setAuthenticatedUserId(newUserId);

        return {
          migratedEvents: count,
          queuedForSync: count,
        };
      }
    );
  }

  /**
   * Checks whether the current device contains anonymous guest progress.
   */
  async hasAnonymousProgress(): Promise<boolean> {
    const attempts = await this.dexie.attempts.count();
    if (attempts > 0) return true;
    const allMetadata = await this.dexie.syncMetadata.toArray();
    return allMetadata.some((m) => !m.accountUid || isLocalLearner(m.accountUid));
  }

  /**
   * Explicitly clears local anonymous guest progress when the user opts NOT to merge
   * local guest history into a newly created account.
   */
  async clearAnonymousProgress(): Promise<void> {
    await this.dexie.transaction(
      'rw',
      [
        this.dexie.events,
        this.dexie.attempts,
        this.dexie.masteryProfiles,
        this.dexie.learnerProfiles,
        this.dexie.syncQueue,
        this.dexie.syncMetadata,
      ],
      async () => {
        const allMetadata = await this.dexie.syncMetadata.toArray();
        const unlinked = allMetadata.filter(
          (m) => !m.accountUid || isLocalLearner(m.accountUid)
        );
        for (const meta of unlinked) {
          await this.dexie.events.delete(meta.eventId);
          await this.dexie.syncMetadata.delete(meta.eventId);
        }
        await this.dexie.attempts.clear();
        await this.dexie.masteryProfiles.clear();

        const allQueue = await this.dexie.syncQueue.toArray();
        const unlinkedQueue = allQueue.filter(
          (q) => !q.accountUid || isLocalLearner(q.accountUid)
        );
        for (const q of unlinkedQueue) {
          await this.dexie.syncQueue.delete(q.id);
        }
      }
    );
  }
}

export const migrationService = new MigrationService();
